/**
 * Display helpers for the CONFIRM_ON_FILE read-only "on file" cards. These
 * format the prepopulation metadata (epoch timestamps, IANA timezone, ISO
 * country code) that is shown to the participant but never submitted.
 */

/**
 * Safe-parse a prepopulation `values` JSON string into a plain object of ALL
 * keys (display needs non-spec keys like `country` / `lastConfirmedAt` / `time`
 * that the spec-allowlist `parseWorkflowPrepopulation` strips). Returns `{}` on
 * nullish / invalid / non-object input.
 */
import { activeLocale } from "../../../resources/strings";
export const parseOnFile = (
  valuesJson: string | null | undefined,
): Record<string, unknown> => {
  if (!valuesJson) {
    return {};
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(valuesJson);
  } catch {
    return {};
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }
  // Copy own keys into a null-prototype object, dropping prototype-polluting
  // keys — this is untrusted backend JSON (mirrors parseWorkflowPrepopulation).
  const source = parsed as Record<string, unknown>;
  const out: Record<string, unknown> = Object.create(null);
  for (const key of Object.keys(source)) {
    if (key === "__proto__" || key === "constructor" || key === "prototype") {
      continue;
    }
    out[key] = source[key];
  }
  return out;
};

/** Read a string-ish on-file value (numbers/strings → string; else ""). */
export const onFileString = (value: unknown): string =>
  value == null || typeof value === "object" ? "" : String(value);

/** Read a finite numeric epoch (seconds) value, or null. */
const epochSeconds = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/** ISO region code → localized country name (e.g. "US" → "United States"). */
export const countryName = (code: unknown): string => {
  const c = onFileString(code);
  if (!c) {
    return "";
  }
  try {
    /* istanbul ignore next -- DisplayNames.of returns the code (default
       fallback:"code") for unknown regions, so the `?? c` guard is belt-and-braces. */
    return new Intl.DisplayNames(["en"], { type: "region" }).of(c) ?? c;
  } catch {
    return c;
  }
};

/** Format `lastConfirmedAt` (epoch seconds) → "Feb 15, 2026", or null if absent. */
export const formatLastConfirmed = (value: unknown): string | null => {
  const secs = epochSeconds(value);
  if (secs == null) {
    return null;
  }
  return new Date(secs * 1000).toLocaleDateString(activeLocale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Format an appointment from epoch `time` (seconds) + IANA `agencyTimezone`
 * → `{ date: "Thu, March 19, 2026", time: "10:00 AM AKST" }`. Returns null when
 * the time is missing/unparseable so the card can be hidden (Android behavior).
 */
export const formatAppointment = (
  time: unknown,
  agencyTimezone: unknown,
): { date: string; time: string } | null => {
  const secs = epochSeconds(time);
  const tz = onFileString(agencyTimezone);
  // Require both a time AND the agency timezone: without the zone we'd format in
  // the participant's device timezone and silently show the wrong time, so hide
  // the card instead (matches Android's AppointmentViewer).
  if (secs == null || !tz) {
    return null;
  }
  try {
    const d = new Date(secs * 1000);
    const date = new Intl.DateTimeFormat(activeLocale, {
      weekday: "short",
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: tz,
    }).format(d);
    const formattedTime = new Intl.DateTimeFormat(activeLocale, {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
      timeZone: tz,
    }).format(d);
    return { date, time: formattedTime };
  } catch {
    // Invalid timezone → unparseable; hide the card rather than show wrong data.
    return null;
  }
};
